import { RELATED_PERSON_RELATIONSHIPS, type RelatedPersonRelationship } from "@repo/domain";
import { createFieldMap } from "@tanstack/react-form";
import { z } from "zod";
import { withFieldGroup } from "@/shared/forms/app-form";

export const relationshipLabel = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

export const EMPTY_RELATED_PERSON_FIELDS = {
  given: "",
  family: "",
  relationships: [] as RelatedPersonRelationship[],
  email: "",
  phone: "",
  isEmergencyContact: false,
  portalAccess: false,
};
export type RelatedPersonFieldValues = typeof EMPTY_RELATED_PERSON_FIELDS;
export const RELATED_PERSON_FIELD_MAP = createFieldMap(EMPTY_RELATED_PERSON_FIELDS);

export const relatedPersonFieldsSchema = z
  .object({
    given: z.string().trim(),
    family: z.string().trim(),
    relationships: z.array(z.enum(RELATED_PERSON_RELATIONSHIPS)).min(1, "Select at least one relationship"),
    email: z.string().trim().min(1, "Enter an email address").email("Enter a valid email address"),
    phone: z.string().trim(),
    isEmergencyContact: z.boolean(),
    portalAccess: z.boolean(),
  })
  .refine((value) => !!(value.given || value.family), {
    message: "Enter a name",
    path: ["given"],
  });

export function toRelatedPersonDetails(value: RelatedPersonFieldValues) {
  const given = value.given.trim();
  return {
    name: { given: given ? given.split(/\s+/) : [], family: value.family.trim() || undefined },
    relationships: value.relationships,
    email: value.email.trim(),
    phone: value.phone.trim() || undefined,
  };
}

export const RelatedPersonFields = withFieldGroup({
  defaultValues: EMPTY_RELATED_PERSON_FIELDS,
  props: {} as {
    disabled?: boolean;
    labelPrefix?: string;
    relationshipDescription?: string;
    emailLocked?: boolean;
    portalAccessDescription?: string;
    portalAccessDisabled?: boolean;
  },
  render: function Render({
    group,
    disabled,
    labelPrefix,
    relationshipDescription,
    emailLocked,
    portalAccessDescription = "Allow this person to use the portal on behalf of this patient and send a welcome email.",
    portalAccessDisabled,
  }) {
    const label = (text: string) => (labelPrefix ? `${labelPrefix} ${text.toLowerCase()}` : text);
    return (
      <>
        <div className="grid gap-4 sm:grid-cols-2">
          <group.AppField name="given">{(field) => <field.InputField label={label("First name")} />}</group.AppField>
          <group.AppField name="family">{(field) => <field.InputField label={label("Last name")} />}</group.AppField>
        </div>
        <group.AppField name="relationships">
          {(field) => (
            <field.MultiOptionGroupField
              label="Relationship to patient"
              description={relationshipDescription}
              options={RELATED_PERSON_RELATIONSHIPS.map((value) => ({ value, label: relationshipLabel(value) }))}
              isRequired
            />
          )}
        </group.AppField>
        <group.AppField name="email">
          {(field) => <field.InputField label={label("Email")} type="email" isRequired disabled={emailLocked} />}
        </group.AppField>
        {emailLocked && (
          <p className="text-xs text-muted-foreground">
            Turn off portal access and save before changing the sign-in email.
          </p>
        )}
        <group.AppField name="phone">
          {(field) => <field.InputField label={label("Phone (optional)")} type="tel" />}
        </group.AppField>
        <group.AppField name="isEmergencyContact">
          {(field) => <field.SwitchField label="Emergency contact" disabled={disabled} />}
        </group.AppField>
        <group.AppField name="portalAccess">
          {(field) => (
            <field.SwitchField
              label="Portal access"
              description={portalAccessDescription}
              disabled={disabled || portalAccessDisabled}
            />
          )}
        </group.AppField>
      </>
    );
  },
});
